import React, { useCallback, useEffect, useMemo } from 'react'
import { Flex, Stack, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import dayjs from 'dayjs'

import { useModal } from 'src/context/ModalContext'

import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomModal from 'src/components/CustomComponents/CustomModal'

import { capitalizeFirstLetter } from 'src/utils/stringUlti'

import { ConversationData } from 'src/interfaces'
import {
	createCategoryTrails,
	updateCategory,
} from 'src/apis/categoryTrailsAPI'

interface ModalCreateConversationProps {
	open: boolean
	data: ConversationData
	onClose: () => void
	handleGetData: () => void
	pagination: {
		pageIndex: number
		pageSize: number
	}
	handleChangePage: (_value: number) => void
	[key: string]: any
}

function ModalCreateConversation({
	open,
	onClose,
	handleGetData,
	pagination,
	handleChangePage,
	data,
}: ModalCreateConversationProps) {
	const form = useForm({
		initialValues: {
			title: '',
			type: 'CONVERSATION',
		},

		validate: {
			title: (value) => {
				if (!value.trim()) return 'Title cannot be empty'
				if (value.length > 255) return 'Title must be 1-255 characters long'
				return null
			},
		},
		validateInputOnBlur: true,
		validateInputOnChange: true,
	})
	const { openModal, closeModal, openError } = useModal()
	const text = useMemo(() => {
		const { id } = data || {}
		return id ? 'update' : 'create'
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(data)])
	const handleCreateConversation = useCallback(async ({ body, id = null }) => {
		try {
			const res = await (id
				? updateCategory(id, body)
				: createCategoryTrails(body))
			if (res?.data?.code === 200) {
				if (id || pagination.pageIndex === 1) {
					await handleGetData()
				} else {
					await handleChangePage(1)
				}
				openModal({
					type: 'success',
					label: `Conversation has been ${text} successfully `,
					handleClose: () => {
						closeModal()
						onClose()
					},
				})
			}
		} catch (error) {
			openError(error)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	const handleSubmit = useCallback(async (values) => {
		const { sending_unix_timestamp } = values || {}
		const body = {
			...values,
			sending_unix_timestamp: dayjs(sending_unix_timestamp).valueOf(),
		}
		openModal({
			type: 'confirm',
			label: `Do you want to ${text} conversation ?`,
			handleClose: () => closeModal(),
			handleConfirm: () => {
				handleCreateConversation({ body, id: data?.id || null })
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	useEffect(() => {
		if (data) {
			const { title = '' } = data
			form.setValues({
				title,
			})
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(data)])
	return (
		<CustomModal
			// size="auto"

			opened={open}
			onClose={onClose}
		>
			<form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
				<Stack
					spacing="0.75 rem"
					sx={{
						paddingTop: 10,
						paddingBottom: 10,
						height: 'fit-content',
					}}
				>
					<Flex sx={{ fontSize: 25, fontWeight: 700 }} justify="center">
						{capitalizeFirstLetter(text)} conversation
					</Flex>
					<TextInput
						autoComplete="off"
						withAsterisk
						key="title"
						label="Title"
						name="title"
						placeholder="Title"
						maxLength={50}
						{...form.getInputProps('title')}
					/>

					<CustomButton
						variant="outlined"
						type="submit"
						sx={{ marginTop: '10px' }}
					>
						{text}
					</CustomButton>
				</Stack>
			</form>
		</CustomModal>
	)
}

export default React.memo(ModalCreateConversation)
